import { useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Flex, HStack, Text, Tooltip, Button } from '@chakra-ui/react';
import AGGridMain from 'components/tables/AGGrid/AGGridMain';
import ActionsButton from 'components/tables/AGGrid/ActionsButton';
import useColumnState from 'hooks/useColumnState';
import { useQuery } from 'hooks/useCustomQuery';
import { PlatformEnums } from 'utils/platforms';
import StateToasts from './StateToasts';
import ServiceProvidersCreate from './Create';
import ServiceProvidersEdit from './Edit';
import walkthroughIds from './walkthroughIds';
import { getServiceProviders } from './api';

const { ufAddServiceProviderBtn } = walkthroughIds;

function ServiceProvidersHeader({ setServiceProvidersAction }) {
    return (
        <div style={{ paddingLeft: '20px' }}>
            <HStack spacing={2}>
                <Tooltip
                    label="Service Provider"
                    placement="bottom"
                    hasArrow
                    portalProps={{ containerRef: undefined }}>
                    <div
                        style={{
                            position: 'relative',
                            display: 'inline-block',
                        }}>
                        <Button
                            size="sm"
                            width="160px"
                            onClick={() => setServiceProvidersAction('Create')}
                            data-walkthroughid={ufAddServiceProviderBtn}>
                            + Service Provider
                        </Button>
                    </div>
                </Tooltip>
            </HStack>
        </div>
    );
}

export default function ServiceProvidersIndex() {
    const [selectedRowData, setSelectedRowData] = useState();
    const [serviceProvidersAction, setServiceProvidersAction] = useState('Get');
    const [successAction, setSuccessAction] = useState(null);
    const tableRef = useRef(null);

    const { darkMode } = useSelector((state) => state.settings);
    const { requiredScope } = useSelector((state) => state.login);

    const platformId = PlatformEnums.EIGHTBYEIGHT;

    const {
        data: serviceProviders = [],
        isLoading: isProvidersLoading,
        isFetching: isProvidersFetching,
        refetch,
    } = useQuery({
        queryKey: ['8x8ServiceProviders'],
        queryFn: () => getServiceProviders(requiredScope).then((r) => r.data),
        staleTime: 0,
        cacheTime: 0,
        refetchOnWindowFocus: false,
    });

    const isLoading = isProvidersLoading;

    // Wrap action setter so returning to 'Get' always triggers a fresh fetch
    const handleSetAction = (action) => {
        setServiceProvidersAction(action);
        if (action === 'Get') refetch();
    };

    const baseColumns = useMemo(
        () => [
            { headerName: 'Name', field: 'name' },
            { headerName: '8x8 Organization ID', field: 'organizationId' },
            {
                headerName: 'API Key',
                field: 'apiKey',
                cellStyle: { fontFamily: 'monospace', fontSize: '12px' },
            },
            { headerName: 'Key Expires (UTC)', field: 'keyExpiry' },
            {
                headerName: 'Action',
                field: 'edit',
                maxWidth: 100,
                filter: false,
                suppressMovable: true,
                lockVisible: true,
                suppressColumnsToolPanel: true,
                suppressHeaderMenuButton: true,
                suppressHeaderFilterButton: true,
                enableRowGroup: false,
                resizable: false,
                sortable: false,
                cellStyle: () => ({ display: 'flex', alignItems: 'center' }),
                lockPosition: 'right',
                cellRenderer: (params) => (
                    <ActionsButton
                        actions={[
                            {
                                label: 'Edit',
                                onClick: () => editServiceProvider(params.data),
                            },
                        ]}
                    />
                ),
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    const { columns, onColumnVisible } = useColumnState(baseColumns);

    const tableDataObj = useMemo(
        () => ({
            columns,
            data: serviceProviders,
        }),
        [columns, serviceProviders],
    );

    function editServiceProvider(rowData) {
        setSelectedRowData(rowData);
        setServiceProvidersAction('Edit');
    }

    return (
        <>
            {serviceProvidersAction === 'Get' && (
                <>
                    <StateToasts type={successAction} />
                    <Text
                        sx={{
                            fontSize: '20',
                            fontWeight: '500',
                            marginBottom: '15px',
                        }}>
                        Service Providers
                    </Text>
                    <AGGridMain
                        gridRef={tableRef}
                        tableData={tableDataObj}
                        masterDetail={false}
                        isLoading={isLoading}
                        isFetching={isProvidersFetching}
                        titleName="Service Providers"
                        refetch={refetch}
                        onColumnVisible={onColumnVisible}
                        extraComponents={
                            <Flex gap="4">
                                <ServiceProvidersHeader
                                    setServiceProvidersAction={handleSetAction}
                                />
                            </Flex>
                        }
                    />
                </>
            )}
            {serviceProvidersAction === 'Create' && (
                <ServiceProvidersCreate
                    setServiceProvidersAction={handleSetAction}
                    setSuccessAction={setSuccessAction}
                    platformId={platformId}
                    requiredScope={requiredScope}
                />
            )}
            {serviceProvidersAction === 'Edit' && (
                <ServiceProvidersEdit
                    rowData={selectedRowData}
                    setServiceProvidersAction={handleSetAction}
                    setSuccessAction={setSuccessAction}
                    darkMode={darkMode}
                    requiredScope={requiredScope}
                />
            )}
        </>
    );
}
